import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {CommandBarButton, Dialog, DialogType, FontIcon, IconButton, Stack, Text} from '@fluentui/react'
import {ErrorCircleRegular, ShieldLockRegular, SquareFilled} from '@fluentui/react-icons'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import uuid from 'react-uuid'
import {isEmpty} from 'lodash'
import DOMPurify from 'dompurify'
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter'
import {nord} from 'react-syntax-highlighter/dist/esm/styles/prism'

import styles from './Chat.module.css'
import Tufin_logo from '../../assets/tufin.png'
import {XSSAllowTags} from '../../constants/sanatizeAllowables'

import {
  ChatConversationResponse,
  ChatHistoryLoadingState,
  ChatMessage,
  ChatMsgResponse,
  Citation,
  Conversation,
  conversationApi,
  ConversationRequest,
  CosmosDBStatus,
  ErrorMessage,
  ExecResults,
  getUserInfo,
  historyClear,
  historyUpdate,
  LoginRequest, messageApi, MessageRequest,
} from "../../api";
import {Answer} from "../../components/Answer";
import {QuestionInput} from "../../components/QuestionInput";
import {ChatHistoryPanel} from "../../components/ChatHistory/ChatHistoryPanel";
import {AppStateContext} from "../../state/AppProvider";
import {useBoolean} from "@fluentui/react-hooks";
import {BouncingDotsLoader} from "../../components/BouncingDotsLoader/BouncingDotsLoader";

const enum messageStatus {
  NotRunning = 'Not Running',
  Processing = 'Processing',
  Done = 'Done'
}

const Chat = () => {
  const appStateContext = useContext(AppStateContext)
  const ui = appStateContext?.state.frontendSettings?.ui
  const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false)
  const [activeCitation, setActiveCitation] = useState<Citation>()
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false)
  const [isIntentsPanelOpen, setIsIntentsPanelOpen] = useState<boolean>(false)
  const abortFuncs = useRef([] as AbortController[])
  const [showAuthMessage, setShowAuthMessage] = useState<boolean | undefined>()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [execResults, setExecResults] = useState<ExecResults[]>([])
  const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning)
  const [clearingChat, setClearingChat] = useState<boolean>(false)
  const [hideErrorDialog, {toggle: toggleErrorDialog}] = useBoolean(true)
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
  const [logo, setLogo] = useState('')
  const [supportedTopicsExpanded, setSupportedTopicsExpanded] = useState(false);

  const toggleIsSupportedTopicsOpen = () => {
    setSupportedTopicsExpanded(!supportedTopicsExpanded);
  };

  const handleSupportedTopicsChevronClick = () => {
    setSupportedTopicsExpanded(!supportedTopicsExpanded);
  };

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: 'Close',
    subText: errorMsg?.subtitle
  }

  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: {main: {maxWidth: 450}}
  }

  const [ASSISTANT, TOOL, ERROR] = ['assistant', 'tool', 'error']
  const NO_CONTENT_ERROR = 'No content in messages object.'

  const handleErrorDialogClose = () => {
    toggleErrorDialog()
    setTimeout(() => {
      setErrorMsg(null)
    }, 500)
  }

  useEffect(() => {
    if (!appStateContext?.state.isLoading) {
      setLogo(ui?.chat_logo || ui?.logo || Tufin_logo)
    }
  }, [appStateContext?.state.isLoading])

  useEffect(() => {
    setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
  }, [appStateContext?.state.chatHistoryLoadingState])

  const getUserInfoList = async () => {
    if (!AUTH_ENABLED) {
      setShowAuthMessage(false)
      return
    }
    const userInfoList = await getUserInfo()
    if (userInfoList.length === 0 && window.location.hostname !== '127.0.0.1') {
      setShowAuthMessage(true)
    } else {
      setShowAuthMessage(false)
    }
  }

  let assistantMessage = {} as ChatMessage
  let toolMessage = {} as ChatMessage
  let assistantContent = ''

  const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string) => {
    if (resultMessage.role === ASSISTANT) {
      assistantContent += resultMessage.content
      assistantMessage = resultMessage
      assistantMessage.content = assistantContent

      if (resultMessage.context) {
        toolMessage = {
          id: uuid(),
          role: TOOL,
          content: resultMessage.context,
          date: new Date().toISOString(),
          sources: [],
          supported_topics: [],
          tracer: "",
        }
      }
    }

    if (resultMessage.role === TOOL) toolMessage = resultMessage

    if (!conversationId) {
      isEmpty(toolMessage)
          ? setMessages([...messages, userMessage, assistantMessage])
          : setMessages([...messages, userMessage, toolMessage, assistantMessage])
    } else {
      isEmpty(toolMessage)
          ? setMessages([...messages, assistantMessage])
          : setMessages([...messages, toolMessage, assistantMessage])
    }
  }

  async function sendMessage(request: ConversationRequest, userData: LoginRequest, abortController: AbortController) {
    const conversationRequest: ConversationRequest = {
      conversation_id: request.conversation_id,
      messages: request.messages,
    };

    try {
      const res = await conversationApi(conversationRequest, userData, abortController.signal)
      if (res.status === 200 && res.body) {
        let reader = res.body.getReader();
        let result = {} as ChatConversationResponse
        let runningText = ''
        while (true) {
          const {done, value} = await reader.read()
          if (done) break
          let text = new TextDecoder('utf-8').decode(value)
          runningText += text
        }
        result = JSON.parse(runningText)
        return result
      } else if (res.status === 400 && res.body) {
        let reader = res.body.getReader();
        let runningText = ''
        while (true) {
          const {done, value} = await reader.read()
          if (done) break
          let text = new TextDecoder('utf-8').decode(value)
          runningText += text
        }
        throw Error(runningText)
      } else {
        throw Error('Failed to send message')
      }
    } catch (err) {
      console.error('Error sending message:', err)
      throw err
    }
  }

  const makeApiRequest = async (question: string, conversationId?: string) => {
    setIsLoading(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: question,
      date: new Date().toISOString(),
      sources: [],
      supported_topics: [],
      tracer:"",
    }

    let conversation: Conversation | null | undefined
    if (!conversationId) {
      conversation = {
        id: conversationId ?? uuid(),
        title: question,
        messages: [userMessage],
        date: new Date().toISOString()
      }
    } else {
      conversation = appStateContext?.state?.currentChat
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoading(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
      }
    }

    appStateContext?.dispatch({type: 'UPDATE_CURRENT_CHAT', payload: conversation})
    setMessages(conversation.messages)
    const user = localStorage.getItem('user');
    if (!user) {
      console.error('User not found.')
      setIsLoading(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      return
    }
    let userData = JSON.parse(user)

    let result = {} as ChatMsgResponse
    try {
      const conversationRequest: ConversationRequest = {
        conversation_id: conversation.id,
        messages: [...conversation.messages.filter(answer => answer.role !== ERROR)]
      }
      let msg_id = (await sendMessage(conversationRequest, userData, abortController)).msg_id

      const messageRequest: MessageRequest = {
        conversation_id: conversation.id,
        message_id: msg_id
      }
      const maxReties = 60
      let retries = 0
      await new Promise(resolve => setTimeout(resolve, 2000))
      let clonedResponse
      while (retries < maxReties){
        let response = await messageApi(messageRequest, userData, abortController.signal)
        clonedResponse = response.clone()
        if (response.ok && response?.body) {
          const reader = response.body.getReader()
          let runningText = ''
          while (true) {
            const {done, value} = await reader.read()
            if (done) break
            var text = new TextDecoder('utf-8').decode(value)
            runningText += text
          }
          result = JSON.parse(runningText)
          if (result.response_ready) break;
        }
        await new Promise(resolve => setTimeout(resolve, 1000))
        retries++
      }
      if(retries === maxReties){
        throw Error('Request timeout waiting for response')
      }
      if (clonedResponse?.body) {
        const reader = clonedResponse.body.getReader()

        let runningText = ''
        while (true) {
          const {done, value} = await reader.read()
          if (done) break

          var text = new TextDecoder('utf-8').decode(value)
          const objects = text.split('\n')
          objects.forEach(obj => {
            try {
              if (obj !== '' && obj !== '{}') {
                runningText += obj
                result = JSON.parse(runningText)
                if (result.content?.length > 0) {
                  setShowLoadingMessage(false)
                  let chatMsg: ChatMessage = {
                    id: result.msg_id,
                    role: 'assistant',
                    content: result.content,
                    date: new Date().toISOString(),
                    sources: result.sources,
                    supported_topics: result.supported_topics,
                    tracer: result.tracer,
                  }
                  processResultMessage(chatMsg, userMessage, conversationId)
                } else if (result.error) {
                  throw Error(result.error)
                }
                runningText = ''
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                console.error(e)
                throw e
              } else {
                console.log('Incomplete message. Continuing...')
              }
            }
          })
        }
        conversation.messages.push(toolMessage, assistantMessage)
        appStateContext?.dispatch({type: 'UPDATE_CURRENT_CHAT', payload: conversation})
        setMessages([...messages, toolMessage, assistantMessage])
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage =
            'An error occurred. Please try again. If the problem persists, please contact the site administrator.'
        if (result.error?.message) {
          errorMessage = result.error.message
        } else if (typeof result.error === 'string') {
          errorMessage = result.error
        }

        errorMessage = parseErrorMessage(errorMessage)

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString(),
          sources: [],
          supported_topics: [],
          tracer:"",
        }
        conversation.messages.push(errorChatMsg)
        appStateContext?.dispatch({type: 'UPDATE_CURRENT_CHAT', payload: conversation})
        setMessages([...messages, errorChatMsg])
      } else {
        setMessages([...messages, userMessage])
      }
    } finally {
      setIsLoading(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
    }

    return abortController.abort()
  }

  const clearChat = async () => {
    setClearingChat(true)
    if (appStateContext?.state.currentChat?.id && appStateContext?.state.isCosmosDBAvailable.cosmosDB) {
      let response = await historyClear(appStateContext?.state.currentChat.id)
      if (!response.ok) {
        setErrorMsg({
          title: 'Error clearing current chat',
          subtitle: 'Please try again. If the problem persists, please contact the site administrator.'
        })
        toggleErrorDialog()
      } else {
        appStateContext?.dispatch({
          type: 'DELETE_CURRENT_CHAT_MESSAGES',
          payload: appStateContext?.state.currentChat.id
        })
        appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext?.state.currentChat })
        setActiveCitation(undefined)
        setIsCitationPanelOpen(false)
        setIsIntentsPanelOpen(false)
        setMessages([])
      }
    }
    setClearingChat(false)
  }

  const tryGetRaiPrettyError = (errorMessage: string) => {
    try {
      // Using a regex to extract the JSON part that contains "innererror"
      const match = errorMessage.match(/'innererror': ({.*})\}\}/)
      if (match) {
        // Replacing single quotes with double quotes and converting Python-like booleans to JSON booleans
        const fixedJson = match[1]
            .replace(/'/g, '"')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
        const innerErrorJson = JSON.parse(fixedJson)
        let reason = ''
        // Check if jailbreak content filter is the reason of the error
        const jailbreak = innerErrorJson.content_filter_result.jailbreak
        if (jailbreak.filtered === true) {
          reason = 'Jailbreak'
        }

        // Returning the prettified error message
        if (reason !== '') {
          return (
              'The prompt was filtered due to triggering Azure OpenAI’s content filtering system.\n' +
              'Reason: This prompt contains content flagged as ' +
              reason +
              '\n\n' +
              'Please modify your prompt and retry. Learn more: https://go.microsoft.com/fwlink/?linkid=2198766'
          )
        }
      }
    } catch (e) {
      console.error('Failed to parse the error:', e)
    }
    return errorMessage
  }

  const parseErrorMessage = (errorMessage: string) => {
    let errorCodeMessage = errorMessage.substring(0, errorMessage.indexOf('-') + 1)
    const innerErrorCue = "{\\'error\\': {\\'message\\': "
    if (errorMessage.includes(innerErrorCue)) {
      try {
        let innerErrorString = errorMessage.substring(errorMessage.indexOf(innerErrorCue))
        if (innerErrorString.endsWith("'}}")) {
          innerErrorString = innerErrorString.substring(0, innerErrorString.length - 3)
        }
        innerErrorString = innerErrorString.replaceAll("\\'", "'")
        let newErrorMessage = errorCodeMessage + ' ' + innerErrorString
        errorMessage = newErrorMessage
      } catch (e) {
        console.error('Error parsing inner error message: ', e)
      }
    }

    return tryGetRaiPrettyError(errorMessage)
  }

  const newChat = () => {
    setProcessMessages(messageStatus.Processing)
    setMessages([])
    setIsCitationPanelOpen(false)
    setIsIntentsPanelOpen(false)
    setActiveCitation(undefined)
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
    setProcessMessages(messageStatus.Done)
  }

  const stopGenerating = () => {
    abortFuncs.current.forEach(a => a.abort())
    setShowLoadingMessage(false)
    setIsLoading(false)
  }

  useEffect(() => {
    if (appStateContext?.state.currentChat) {
      setMessages(appStateContext.state.currentChat.messages)
    } else {
      setMessages([])
    }
  }, [appStateContext?.state.currentChat])

  useLayoutEffect(() => {
    const saveToDB = async (messages: ChatMessage[], id: string) => {
      const response = await historyUpdate(messages, id)
      return response
    }

    if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
      if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
        if (!appStateContext?.state.currentChat?.messages) {
          console.error('Failure fetching current chat state.')
          return
        }
        const noContentError = appStateContext.state.currentChat.messages.find(m => m.role === ERROR)

        if (!noContentError?.content.includes(NO_CONTENT_ERROR)) {
          saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
              .then(res => {
                if (!res.ok) {
                  let errorMessage =
                      "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator."
                  let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString(),
                    sources: [],
                    supported_topics: [], 
                    tracer:"",
                  }
                  if (!appStateContext?.state.currentChat?.messages) {
                    let err: Error = {
                      ...new Error(),
                      message: 'Failure fetching current chat state.'
                    }
                    throw err
                  }
                  setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
                }
                return res as Response
              })
              .catch(err => {
                console.error('Error: ', err)
                let errRes: Response = {
                  ...new Response(),
                  ok: false,
                  status: 500
                }
                return errRes
              })
        }
      } else {
      }
      appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat })
      setMessages(appStateContext.state.currentChat.messages)
      setProcessMessages(messageStatus.NotRunning)
    }
  }, [processMessages])

  useEffect(() => {
    if (AUTH_ENABLED !== undefined) getUserInfoList()
  }, [AUTH_ENABLED])

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [showLoadingMessage, processMessages])


  const onViewSource = (citation: Citation) => {
    if (citation.url && !citation.url.includes('blob.core')) {
      window.open(citation.url, '_blank')
    }
  }

  const disabledButton = () => {
    return (
        isLoading ||
        (messages && messages.length === 0) ||
        clearingChat ||
        appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    )
  }

  return (
      <div className={styles.container} role="main">
        {showAuthMessage ? (
            <Stack className={styles.chatEmptyState}>
              <ShieldLockRegular
                  className={styles.chatIcon}
                  style={{ color: 'darkorange', height: '200px', width: '200px' }}
              />
              <h1 className={styles.chatEmptyStateTitle}>Authentication Not Configured</h1>
              <h2 className={styles.chatEmptyStateSubtitle}>
                This app does not have authentication configured. Please add an identity provider by finding your app in the{' '}
                <a href="https://portal.azure.com/" target="_blank">
                  Azure Portal
                </a>
                and following{' '}
                <a
                    href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
                    target="_blank">
                  these instructions
                </a>
                .
              </h2>
              <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: '20px' }}>
                <strong>Authentication configuration takes a few minutes to apply. </strong>
              </h2>
              <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: '20px' }}>
                <strong>If you deployed in the last 10 minutes, please wait and reload the page after 10 minutes.</strong>
              </h2>
            </Stack>
        ) : (
            <Stack horizontal className={styles.chatRoot}>
              <div className={styles.chatContainer}>
                {!messages || messages.length < 1 ? (
                    <Stack className={styles.chatEmptyState}>
                      <img src={logo} className={styles.chatIcon} aria-hidden="true" />
                      <h1 className={styles.chatEmptyStateTitle}>{ui?.chat_title}</h1>
                      <h2 className={styles.chatEmptyStateSubtitle}>{ui?.chat_description}</h2>
                      {(ui?.supported_topics?.length ?? 0) > 0 && (
                          <Stack className={styles.supportedTopicsContainer}>
                            <Stack.Item
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? toggleIsSupportedTopicsOpen() : null)}
                                role="button"
                                tabIndex={0}
                                aria-expanded={supportedTopicsExpanded}
                                aria-label="Toggle supported topics"
                            >
                              <Stack horizontal horizontalAlign="center" verticalAlign="center" className={styles.accordionHeader}>
                                <Text
                                    className={styles.accordionTitle}
                                    onClick={toggleIsSupportedTopicsOpen}
                                    role="button"
                                    aria-expanded={supportedTopicsExpanded}
                                >
                                  Available Knowledge Base Topics
                                </Text>
                                <FontIcon
                                    className={styles.accordionIcon}
                                    onClick={handleSupportedTopicsChevronClick}
                                    iconName={supportedTopicsExpanded ? 'ChevronDown' : 'ChevronRight'}
                                />
                              </Stack>
                            </Stack.Item>

                            {supportedTopicsExpanded && (
                                <div className={styles.supportedTopicsList}>
                                  <ul className={styles.topicList}>
                                    {ui?.supported_topics?.map((topic, idx) => (
                                        <li key={idx} className={styles.topicItem}>
                                          {topic}
                                        </li>
                                    ))}
                                  </ul>
                                </div>
                            )}
                          </Stack>
                      )}
                    </Stack>
                ) : (
                    <div className={styles.chatMessageStream} style={{marginBottom: isLoading ? '40px' : '0px'}}  role="log">
                      {messages.map((answer, index) => (
                          <>
                          {answer.role === 'user' ? (
                                <div className={styles.chatMessageUser} tabIndex={0}>
                                  <div className={styles.chatMessageUserMessage}>{answer.content}</div>
                                </div>
                            ) : answer.role === 'assistant' ? (
                                <div className={styles.chatMessageGpt}>
                                  <Answer
                                      answer={{
                                        answer: answer.content,
                                        sources: answer.sources,
                                        message_id: answer.id,
                                        feedback: answer.feedback,
                                        supported_topics: answer.supported_topics,
                                        tracer: answer.tracer,
                                      }}
                                  />
                                </div>
                            ) : answer.role === ERROR ? (
                                <div className={styles.chatMessageError}>
                                  <Stack horizontal className={styles.chatMessageErrorContent}>
                                    <ErrorCircleRegular className={styles.errorIcon}
                                                        style={{color: 'rgba(182, 52, 67, 1)' }} />
                                    <span>Error</span>
                                  </Stack>
                                  <span className={styles.chatMessageErrorContent}>{answer.content}</span>
                                </div>
                            ) : null}
                          </>
                      ))}
                      {showLoadingMessage && (
                          <>
                            <div className={styles.chatMessageGpt}>
                                <BouncingDotsLoader/>
                            </div>
                          </>
                      )}
                      <div ref={chatMessageStreamEnd} />
                    </div>
                )}

                <Stack horizontal className={styles.chatInput}>
                  {isLoading && messages.length > 0 && (
                      <Stack
                          horizontal
                          className={styles.stopGeneratingContainer}
                          role="button"
                          aria-label="Stop generating"
                          tabIndex={0}
                          onClick={stopGenerating}
                          onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? stopGenerating() : null)}>
                        <SquareFilled className={styles.stopGeneratingIcon} aria-hidden="true"/>
                        <span className={styles.stopGeneratingText} aria-hidden="true">
                    Stop generating
                  </span>
                      </Stack>
                  )}
                  <Stack>
                        <CommandBarButton
                            role="button"
                            styles={{
                              icon: {
                                color: '#FFFFFF',
                              },
                              iconDisabled: {
                                color: '#BDBDBD !important',
                              },
                              root: {
                                color: '#FFFFFF',
                                background:
                                    'radial-gradient(109.81% 107.82% at 100.1% 90.19%, #FF7F50 33.63%, #FF8C00 70.31%, #FFA500 100%)',
                              },
                              rootDisabled: {
                                background: '#F0F0F0',
                              },
                            }}
                            className={styles.newChatIcon}
                            iconProps={{ iconName: 'Delete' }}
                            onClick={newChat}
                            disabled={disabledButton()}
                            aria-label="start a new chat button"
                        >
                          {!disabledButton() && (
                              <span className={styles.tooltip}>Reset session</span>
                          )}
                        </CommandBarButton>
                    <Dialog
                        hidden={hideErrorDialog}
                        onDismiss={handleErrorDialogClose}
                        dialogContentProps={errorDialogContentProps}
                        modalProps={modalProps}
                    />
                  </Stack>
                  <QuestionInput
                      clearOnSend
                      placeholder="Type a new question..."
                      disabled={isLoading}
                      onSend={(question, id) => {
                        makeApiRequest(question, id).then(() => {
                          // Handle the successful completion of the API request, if needed.
                        })
                            .catch(error => {
                              console.error("API request failed: ", error);
                            });
                      }}
                      conversationId={
                        appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined
                      }
                  />
                </Stack>
              </div>
              {/* Citation Panel */}
              {messages && messages.length > 0 && isCitationPanelOpen && activeCitation && (
                  <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
                    <Stack
                        aria-label="Citations Panel Header Container"
                        horizontal
                        className={styles.citationPanelHeaderContainer}
                        horizontalAlign="space-between"
                        verticalAlign="center">
                <span aria-label="Citations" className={styles.citationPanelHeader}>
                  Citations
                </span>
                      <IconButton
                          iconProps={{ iconName: 'Cancel' }}
                          aria-label="Close citations panel"
                          onClick={() => setIsCitationPanelOpen(false)}
                      />
                    </Stack>
                    <h5
                        className={styles.citationPanelTitle}
                        tabIndex={0}
                        title={
                          activeCitation.url && !activeCitation.url.includes('blob.core')
                              ? activeCitation.url
                              : activeCitation.title ?? ''
                        }
                        onClick={() => onViewSource(activeCitation)}>
                      {activeCitation.title}
                    </h5>
                    <div tabIndex={0}>
                      <ReactMarkdown
                          linkTarget="_blank"
                          className={styles.citationPanelContent}
                          children={DOMPurify.sanitize(activeCitation.content, { ALLOWED_TAGS: XSSAllowTags })}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                      />
                    </div>
                  </Stack.Item>
              )}
              {messages && messages.length > 0 && isIntentsPanelOpen && (
                  <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Intents Panel">
                    <Stack
                        aria-label="Intents Panel Header Container"
                        horizontal
                        className={styles.citationPanelHeaderContainer}
                        horizontalAlign="space-between"
                        verticalAlign="center">
                <span aria-label="Intents" className={styles.citationPanelHeader}>
                  Intents
                </span>
                      <IconButton
                          iconProps={{ iconName: 'Cancel' }}
                          aria-label="Close intents panel"
                          onClick={() => setIsIntentsPanelOpen(false)}
                      />
                    </Stack>
                    <Stack horizontalAlign="space-between">
                      {execResults.map((execResult) => {
                        return (
                            <Stack className={styles.exectResultList} verticalAlign="space-between">
                              <><span>Intent:</span> <p>{execResult.intent}</p></>
                              {execResult.search_query && <><span>Search Query:</span>
                                <SyntaxHighlighter
                                    style={nord}
                                    wrapLines={true}
                                    lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                                    language="sql"
                                    PreTag="p">
                                  {execResult.search_query}
                                </SyntaxHighlighter></>}
                              {execResult.search_result && <><span>Search Result:</span> <p>{execResult.search_result}</p></>}
                              {execResult.code_generated && <><span>Code Generated:</span>
                                <SyntaxHighlighter
                                    style={nord}
                                    wrapLines={true}
                                    lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                                    language="python"
                                    PreTag="p">
                                  {execResult.code_generated}
                                </SyntaxHighlighter>
                              </>}
                            </Stack>
                        )
                      })}
                    </Stack>
                  </Stack.Item>
              )}
              {appStateContext?.state.isChatHistoryOpen &&
                  appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && <ChatHistoryPanel />}
            </Stack>
        )}
      </div>
  )
}

export default Chat